@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.service

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import org.example.project.enginemodule.moduleAssetUrl
import org.example.project.manager.LumaticaApiManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.attributes.value
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.alignItems
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.cursor
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.flexWrap
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.opacity
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H4
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.MouseEvent
import org.w3c.files.File

data class BildImportItem(
    val id: String,
    val imageUrl: String,
    val label: String = ""
)

object ProfilBildImportService {
    suspend fun loadProfileImages(currentUser: All): List<All> {
        val api = if (currentUser.firmenId.isNotBlank()) {
            "profilverwaltung/documents/${apiPart(currentUser.firmenId)}/${apiPart(currentUser.id)}"
        } else {
            "profilverwaltung/documents/${apiPart(currentUser.id)}"
        }
        val raw = runCatching { LumaticaApiManager.call(api) }.getOrDefault("")
        if (raw.isBlank()) return emptyList()
        return runCatching { ProfilDocumentApi.json.decodeFromString<List<All>>(raw) }
            .getOrDefault(emptyList())
            .filter(::isProfileImageDocument)
    }

    suspend fun uploadProfileImage(currentUser: All, file: File, fileName: String = "avatar_${file.name}"): Boolean =
        if (currentUser.firmenId.isNotBlank()) {
            ProfilDocumentApi.uploadCompanyDocument(currentUser.firmenId, currentUser.id, file, fileName)
        } else {
            ProfilDocumentApi.uploadDocument(currentUser.id, file, fileName)
        }

    suspend fun deleteProfileImage(currentUser: All, fileName: String): Boolean {
        if (currentUser.id.isBlank() || fileName.isBlank()) return false
        return runCatching {
            if (currentUser.firmenId.isNotBlank()) {
                LumaticaApiManager.call("profilverwaltung/delete-document/${apiPart(currentUser.firmenId)}/${apiPart(currentUser.id)}/${apiPart(fileName)}")
            } else {
                LumaticaApiManager.call("profilverwaltung/delete-document/${apiPart(currentUser.id)}/${apiPart(fileName)}")
            }
            true
        }.getOrDefault(false)
    }

    private fun isProfileImageDocument(doc: All): Boolean {
        val fileName = doc.file.lowercase()
        val art = doc.art.lowercase()
        return fileName.endsWith(".png") ||
            fileName.endsWith(".jpg") ||
            fileName.endsWith(".jpeg") ||
            fileName.endsWith(".webp") ||
            art.contains("bild") ||
            art.contains("image") ||
            art.contains("foto") ||
            art.contains("avatar")
    }

    private fun apiPart(value: String): String =
        js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String
}

@Composable
fun BildImportService(
    visible: Boolean,
    title: String,
    items: List<BildImportItem>,
    selectedId: String,
    uploadBusy: Boolean,
    deleteBusy: Boolean,
    onClose: () -> Unit,
    onSelect: (String) -> Unit,
    onUpload: suspend (File) -> Unit,
    onDelete: suspend (String) -> Unit,
    onApply: () -> Unit,
    showTransformControls: Boolean = false,
    placeholderSrc: String = "images/user.svg",
    posX: String = "0",
    posY: String = "0",
    zoom: String = "1.15",
    onPosXChange: (String) -> Unit = {},
    onPosYChange: (String) -> Unit = {},
    onZoomChange: (String) -> Unit = {},
    onInfo: (String) -> Unit = {}
) {
    if (!visible) return

    val scope = rememberCoroutineScope()
    var filePicker by remember(title) { mutableStateOf<HTMLInputElement?>(null) }
    val selectedItem = items.firstOrNull { it.id == selectedId } ?: items.firstOrNull()
    val previewSrc = selectedItem?.imageUrl.orEmpty()
    val previewOffsetX = posX.toDoubleOrNull() ?: 0.0
    val previewOffsetY = posY.toDoubleOrNull() ?: 0.0
    val previewZoom = zoom.ifBlank { "1.15" }
    var dragging by remember(selectedItem?.id) { mutableStateOf(false) }
    var dragStartX by remember(selectedItem?.id) { mutableStateOf(0.0) }
    var dragStartY by remember(selectedItem?.id) { mutableStateOf(0.0) }
    var dragOriginPosX by remember(selectedItem?.id) { mutableStateOf(0.0) }
    var dragOriginPosY by remember(selectedItem?.id) { mutableStateOf(0.0) }

    fun startDrag(clientX: Double, clientY: Double) {
        if (!showTransformControls) return
        dragging = true
        dragStartX = clientX
        dragStartY = clientY
        dragOriginPosX = posX.toDoubleOrNull() ?: 0.0
        dragOriginPosY = posY.toDoubleOrNull() ?: 0.0
    }

    fun updateDrag(clientX: Double, clientY: Double) {
        if (!dragging || !showTransformControls) return
        onPosXChange((dragOriginPosX + clientX - dragStartX).toInt().toString())
        onPosYChange((dragOriginPosY + clientY - dragStartY).toInt().toString())
    }

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            backgroundColor(org.jetbrains.compose.web.css.rgba(0, 0, 0, 0.72))
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(18.px)
            property("z-index", "120000")
            property("box-sizing", "border-box")
        }
        onClick { onClose() }
    }) {
        Div({
            style {
                width(100.percent)
                property("max-width", "720px")
                maxHeight(88.vh)
                property("overflow-y", "auto")
                padding(22.px)
                borderRadius(22.px)
                property("background", "linear-gradient(180deg, rgba(17,21,29,0.98) 0%, rgba(23,28,38,0.98) 100%)")
                property("border", "1px solid rgba(255,255,255,0.14)")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
                property("box-sizing", "border-box")
            }
            onClick { it.stopPropagation() }
        }) {
            H4({ style { margin(0.px); color(Color.white); fontSize(24.px) } }) { Text(title) }

            Input(InputType.File, attrs = {
                accept(".png,.jpg,.jpeg,.webp")
                ref { element ->
                    filePicker = element
                    onDispose { filePicker = null }
                }
                style { display(DisplayStyle.None) }
                onChange {
                    val input = it.target as? HTMLInputElement ?: return@onChange
                    val file = input.files?.item(0) ?: return@onChange
                    scope.launch {
                        try {
                            onUpload(file)
                        } finally {
                            input.value = ""
                        }
                    }
                }
            })

            if (showTransformControls) {
                Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center) } }) {
                    Div({
                        style {
                            width(196.px)
                            height(196.px)
                            property("overflow", "hidden")
                            position(Position.Relative)
                            property("background", "rgba(255,255,255,0.04)")
                            property("border", "1px solid rgba(255,255,255,0.14)")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            borderRadius(999.px)
                            property("box-shadow", "0 12px 28px rgba(0,0,0,0.28)")
                        }
                        onMouseDown {
                            val event = it.nativeEvent as? MouseEvent ?: return@onMouseDown
                            startDrag(event.clientX.toDouble(), event.clientY.toDouble())
                        }
                        onMouseMove {
                            val event = it.nativeEvent as? MouseEvent ?: return@onMouseMove
                            updateDrag(event.clientX.toDouble(), event.clientY.toDouble())
                        }
                        onMouseUp { dragging = false }
                        onMouseLeave { dragging = false }
                        onTouchStart {
                            val event = it.nativeEvent as? TouchEvent ?: return@onTouchStart
                            val touch = event.touches.item(0) ?: return@onTouchStart
                            startDrag(touch.clientX.toDouble(), touch.clientY.toDouble())
                        }
                        onTouchMove {
                            val event = it.nativeEvent as? TouchEvent ?: return@onTouchMove
                            val touch = event.touches.item(0) ?: return@onTouchMove
                            updateDrag(touch.clientX.toDouble(), touch.clientY.toDouble())
                        }
                        onTouchEnd { dragging = false }
                    }) {
                        if (previewSrc.isNotBlank()) {
                            Img(src = previewSrc, attrs = {
                                style {
                                    position(Position.Absolute)
                                    left(50.percent)
                                    top(50.percent)
                                    property("width", "auto")
                                    property("height", "auto")
                                    property("max-width", "100%")
                                    property("max-height", "100%")
                                    property("object-fit", "contain")
                                    property(
                                        "transform",
                                        "translate(calc(-50% + ${previewOffsetX}px), calc(-50% + ${previewOffsetY}px)) scale($previewZoom)"
                                    )
                                    property("transform-origin", "center center")
                                }
                            })
                        } else {
                            Img(src = placeholderSrc, attrs = {
                                style {
                                    width(74.px)
                                    height(74.px)
                                    opacity(0.9)
                                    property("filter", "brightness(0) invert(1)")
                                }
                            })
                        }
                    }
                }

                BildImportRange("Zoom", ((zoom.toDoubleOrNull() ?: 1.15) * 100).toInt().toString(), "20", "250") {
                    val next = (it.toDoubleOrNull() ?: 115.0) / 100.0
                    onZoomChange(next.toString())
                }
                BildImportRange("Horizontal", (posX.toDoubleOrNull() ?: 0.0).toInt().toString(), "-140", "140") {
                    onPosXChange((it.toDoubleOrNull() ?: 0.0).toInt().toString())
                }
                BildImportRange("Vertikal", (posY.toDoubleOrNull() ?: 0.0).toInt().toString(), "-140", "140") {
                    onPosYChange((it.toDoubleOrNull() ?: 0.0).toInt().toString())
                }
                Span({ style { color(Color("#9aa8bc")); fontSize(12.px) } }) {
                Text("Bild ziehen oder die Regler für Zoom, X und Y verwenden.")
                }
            }

            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.SpaceBetween); alignItems(AlignItems.Center); gap(12.px); flexWrap(FlexWrap.Wrap) } }) {
                Span({ style { color(Color.white); fontSize(16.px); property("font-weight", "700") } }) { Text("Galerie") }
                Div({ style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                    LumaticaButton(
                        label = if (uploadBusy) "Upload..." else "Bild hochladen",
                        onClick = {
                            filePicker?.value = ""
                            filePicker?.click()
                        },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = !uploadBusy && !deleteBusy
                    )
                    LumaticaButton(
                        label = if (deleteBusy) "Löschen..." else "Bild löschen",
                        onClick = {
                            val targetId = selectedItem?.id.orEmpty()
                            if (targetId.isBlank()) return@LumaticaButton
                            scope.launch { onDelete(targetId) }
                        },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = selectedItem != null && !uploadBusy && !deleteBusy
                    )
                }
            }

            if (items.isEmpty()) {
                P({ style { margin(0.px); color(Color("#9aa8bc")); fontSize(13.px) } }) { Text("Noch keine Bilder vorhanden.") }
            } else {
                Div({
                    style {
                        property("max-height", "34vh")
                        property("overflow-y", "auto")
                        padding(0.px, 4.px, 0.px, 0.px)
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Grid)
                            property("grid-template-columns", "repeat(auto-fit, minmax(110px, 1fr))")
                            gap(10.px)
                        }
                    }) {
                        items.forEach { item ->
                            val isSelected = item.id == selectedItem?.id
                            Div({
                                style {
                                    height(118.px)
                                    borderRadius(14.px)
                                    property("border", if (isSelected) "2px solid rgba(143,210,255,0.95)" else "1px solid rgba(255,255,255,0.10)")
                                    position(Position.Relative)
                                    property("overflow", "hidden")
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.FlexEnd)
                                    justifyContent(JustifyContent.Center)
                                    padding(0.px, 0.px, 8.px, 0.px)
                                    property("box-sizing", "border-box")
                                    cursor("pointer")
                                    property("background", "rgba(255,255,255,0.03)")
                                }
                                onClick { onSelect(item.id) }
                            }) {
                                Img(src = item.imageUrl, attrs = {
                                    style {
                                        position(Position.Absolute)
                                        left(0.px)
                                        top(0.px)
                                        width(100.percent)
                                        height(100.percent)
                                        property("object-fit", "contain")
                                        property("background", "rgba(9,12,18,0.55)")
                                    }
                                })
                                Span({
                                    style {
                                        color(Color("#dbe7ff"))
                                        fontSize(11.px)
                                        backgroundColor(Color("rgba(0,0,0,0.45)"))
                                        padding(3.px, 8.px)
                                        borderRadius(999.px)
                                    }
                                }) { Text(if (isSelected) "Aktiv" else item.label.ifBlank { "Bild" }) }
                            }
                        }
                    }
                }
            }

            Div({ style { display(DisplayStyle.Flex); justifyContent(JustifyContent.FlexEnd); gap(10.px); flexWrap(FlexWrap.Wrap) } }) {
                LumaticaButton(
                    label = "Zurück",
                    onClick = onClose,
                    variant = LumaticaButtonVariant.Standard,
                    enabled = !uploadBusy
                )
                LumaticaButton(
                    label = "Übernehmen",
                    onClick = {
                        onApply()
                    onInfo("Bild gewählt.")
                    },
                    variant = LumaticaButtonVariant.Action,
                    enabled = selectedItem != null && !uploadBusy && !deleteBusy
                )
            }
        }
    }
}

@Composable
private fun BildImportRange(label: String, currentValue: String, min: String, max: String, onInputValue: (String) -> Unit) {
    Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(6.px) } }) {
        Span({ style { color(Color("#d7e1ef")); fontSize(13.px); property("font-weight", "600") } }) { Text(label) }
        Input(InputType.Range, attrs = {
            value(currentValue)
            attr("min", min)
            attr("max", max)
            attr("step", "1")
            onInput { onInputValue(it.value.toString()) }
            style { width(100.percent) }
        })
    }
}

@Composable
fun ModuleImageUploadService(
    visible: Boolean,
    targetAssetKey: String?,
    onClose: () -> Unit,
    onLoadModuleAssets: suspend () -> List<Int>,
    onUploadModuleAsset: suspend (File, String) -> Int?,
    onDeleteModuleAsset: suspend (Int) -> Boolean,
    onAssetSelected: (Int, String?) -> Unit,
    onAssetDeleted: (Int, String?) -> Unit = { _, _ -> },
    onInfo: (String) -> Unit = {}
) {
    if (!visible) return

    val scope = rememberCoroutineScope()
    var moduleAssetIds by remember(targetAssetKey) { mutableStateOf<List<Int>>(emptyList()) }
    var assetListBusy by remember(targetAssetKey) { mutableStateOf(false) }
    var uploadBusy by remember(targetAssetKey) { mutableStateOf(false) }
    var deleteBusy by remember(targetAssetKey) { mutableStateOf(false) }
    var assetUploadPicker by remember(targetAssetKey) { mutableStateOf<HTMLInputElement?>(null) }
    var selectedAssetId by remember(targetAssetKey) { mutableStateOf<Int?>(null) }
    var panelLeft by remember(targetAssetKey) { mutableStateOf(((window.innerWidth.toDouble() - 760.0) / 2.0).coerceAtLeast(24.0)) }
    var panelTop by remember(targetAssetKey) { mutableStateOf(88.0) }
    var dragActive by remember(targetAssetKey) { mutableStateOf(false) }
    var dragOffsetX by remember(targetAssetKey) { mutableStateOf(0.0) }
    var dragOffsetY by remember(targetAssetKey) { mutableStateOf(0.0) }

    LaunchedEffect(visible, targetAssetKey) {
        if (!visible) return@LaunchedEffect
        assetListBusy = true
        try {
            moduleAssetIds = onLoadModuleAssets()
        } finally {
            assetListBusy = false
        }
    }

    LaunchedEffect(moduleAssetIds) {
        if (selectedAssetId !in moduleAssetIds) selectedAssetId = moduleAssetIds.firstOrNull()
    }

    BildImportService(
        visible = visible,
        title = "Asset Galerie (Module)",
        items = moduleAssetIds.map { assetId ->
            BildImportItem(
                id = assetId.toString(),
                imageUrl = moduleAssetUrl(assetId),
                label = "#$assetId"
            )
        },
        selectedId = selectedAssetId?.toString().orEmpty(),
        uploadBusy = uploadBusy,
        deleteBusy = deleteBusy,
        onClose = onClose,
        onSelect = { selectedAssetId = it.toIntOrNull() },
        onUpload = { file ->
            uploadBusy = true
            try {
                val uploadedId = onUploadModuleAsset(file, file.name)
                if (uploadedId != null) {
                    moduleAssetIds = (moduleAssetIds + uploadedId).distinct().sorted()
                    selectedAssetId = uploadedId
                    onInfo("Asset hochgeladen: $uploadedId")
                } else {
                    onInfo("Asset-Upload fehlgeschlagen.")
                }
            } finally {
                uploadBusy = false
            }
        },
        onDelete = { id ->
            val assetId = id.toIntOrNull() ?: return@BildImportService
            deleteBusy = true
            try {
                val deleted = onDeleteModuleAsset(assetId)
                if (deleted) {
                    moduleAssetIds = moduleAssetIds.filterNot { it == assetId }
                    onAssetDeleted(assetId, targetAssetKey)
                    selectedAssetId = moduleAssetIds.firstOrNull { it != assetId }
                    onInfo("Asset gelöscht: $assetId")
                } else {
                    onInfo("Asset konnte nicht gelöscht werden.")
                }
            } finally {
                deleteBusy = false
            }
        },
        onApply = {
            val assetId = selectedAssetId ?: return@BildImportService
            onAssetSelected(assetId, targetAssetKey)
                    onInfo("Asset gewählt: $assetId")
            onClose()
        }
    )
    return

    Div({
        style {
            position(Position.Fixed)
            property("inset", "0")
            backgroundColor(org.jetbrains.compose.web.css.rgba(0, 0, 0, 0.42))
            property("z-index", "5200")
        }
        onMouseMove { event ->
            if (!dragActive) return@onMouseMove
            val mouseEvent = event.nativeEvent as? MouseEvent ?: return@onMouseMove
            panelLeft = (mouseEvent.clientX - dragOffsetX).coerceAtLeast(16.0)
            panelTop = (mouseEvent.clientY - dragOffsetY).coerceAtLeast(16.0)
        }
        onMouseUp { dragActive = false }
        onMouseLeave { dragActive = false }
        onClick { onClose() }
    }) {
        Div({
            style {
                position(Position.Fixed)
                left(panelLeft.px)
                top(panelTop.px)
                width(760.px)
                maxHeight(78.vh)
                property("overflow-y", "auto")
                backgroundColor(Color("#11151d"))
                borderRadius(14.px)
                property("border", "2px solid rgba(166,191,255,0.32)")
                property("outline", "1px solid rgba(255,255,255,0.08)")
                padding(14.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(10.px)
                property("box-shadow", "0 26px 70px rgba(0,0,0,0.48)")
            }
            onClick { it.stopPropagation() }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    padding(0.px, 0.px, 10.px, 0.px)
                    property("border-bottom", "1px solid rgba(255,255,255,0.08)")
                    cursor(if (dragActive) "grabbing" else "grab")
                }
                onMouseDown { event ->
                    val mouseEvent = event.nativeEvent as? MouseEvent ?: return@onMouseDown
                    dragActive = true
                    dragOffsetX = mouseEvent.clientX - panelLeft
                    dragOffsetY = mouseEvent.clientY - panelTop
                }
            }) {
                H4({ style { margin(0.px); color(Color("#f4f6f8")) } }) { Text("Asset Galerie (Module)") }
                Div({ style { display(DisplayStyle.Flex); gap(8.px); alignItems(AlignItems.Center) } }) {
                    Span({ style { color(Color("#8fa0b8")); fontSize(12.px) } }) { Text("Fenster verschiebbar") }
                    LumaticaButton(
                        label = "Schließen",
                        onClick = { onClose() },
                        variant = LumaticaButtonVariant.Standard
                    )
                }
            }

            Div({ style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                Input(InputType.File, attrs = {
                    accept(".png,.jpg,.jpeg,.webp")
                    ref { element ->
                        assetUploadPicker = element
                        onDispose { assetUploadPicker = null }
                    }
                    style { display(DisplayStyle.None) }
                    onInput {
                        val file = (it.target as? HTMLInputElement)?.files?.item(0) ?: return@onInput
                        uploadBusy = true
                        scope.launch {
                            try {
                                val uploadedId = onUploadModuleAsset(file, file.name)
                                if (uploadedId != null) {
                                    moduleAssetIds = (moduleAssetIds + uploadedId).distinct().sorted()
                                    selectedAssetId = uploadedId
                                    onInfo("Asset hochgeladen: $uploadedId")
                                } else {
                                    onInfo("Asset-Upload fehlgeschlagen.")
                                }
                            } finally {
                                uploadBusy = false
                            }
                        }
                    }
                })

                LumaticaButton(
                    label = if (uploadBusy) "Upload..." else "Bild hochladen",
                    onClick = { assetUploadPicker?.click() },
                    variant = LumaticaButtonVariant.Standard
                )
                LumaticaButton(
                    label = "Neu laden",
                    onClick = {
                        scope.launch {
                            assetListBusy = true
                            try {
                                moduleAssetIds = onLoadModuleAssets()
                            } finally {
                                assetListBusy = false
                            }
                        }
                    },
                    variant = LumaticaButtonVariant.Standard,
                    enabled = !assetListBusy
                )
                LumaticaButton(
                    label = "Auswählen",
                    onClick = {
                        val assetId = selectedAssetId ?: return@LumaticaButton
                        onAssetSelected(assetId, targetAssetKey)
                                    onInfo("Asset gewählt: $assetId")
                        onClose()
                    },
                    variant = LumaticaButtonVariant.Action,
                    enabled = selectedAssetId != null && !deleteBusy
                )
                LumaticaButton(
                    label = if (deleteBusy) "Löschen..." else "Löschen",
                    onClick = {
                        val assetId = selectedAssetId ?: return@LumaticaButton
                        deleteBusy = true
                        scope.launch {
                            try {
                                val deleted = onDeleteModuleAsset(assetId)
                                if (deleted) {
                                    moduleAssetIds = moduleAssetIds.filterNot { it == assetId }
                                    onAssetDeleted(assetId, targetAssetKey)
                                    selectedAssetId = moduleAssetIds.firstOrNull { it != assetId }
                                    onInfo("Asset gelöscht: $assetId")
                                } else {
                                    onInfo("Asset konnte nicht gelöscht werden.")
                                }
                            } finally {
                                deleteBusy = false
                            }
                        }
                    },
                    variant = LumaticaButtonVariant.Standard,
                    enabled = selectedAssetId != null && !uploadBusy && !deleteBusy
                )
            }

            if (assetListBusy) {
                Span({ style { color(Color("#8a8f9a")); fontSize(12.px) } }) { Text("Lade Assets...") }
            } else if (moduleAssetIds.isEmpty()) {
                Span({ style { color(Color("#8a8f9a")); fontSize(12.px) } }) { Text("Noch keine Assets vorhanden.") }
            } else {
                Div({
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(4, minmax(0, 1fr))")
                        gap(10.px)
                    }
                }) {
                    moduleAssetIds.forEach { assetId ->
                        val imageUrl = moduleAssetUrl(assetId)
                        val isSelected = assetId == selectedAssetId
                        Div({
                            style {
                                height(110.px)
                                borderRadius(10.px)
                                property("border", if (isSelected) "2px solid #8fd2ff" else "1px solid #2a2d34")
                                property("background", "linear-gradient(135deg, rgba(91,155,255,0.25), rgba(14,17,23,0.95)), url('$imageUrl') center/cover no-repeat")
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.FlexEnd)
                                justifyContent(JustifyContent.Center)
                                padding(0.px, 0.px, 6.px, 0.px)
                                property("box-shadow", if (isSelected) "0 0 0 1px rgba(143,210,255,0.35), 0 14px 30px rgba(0,0,0,0.35)" else "none")
                            }
                            onClick {
                                selectedAssetId = assetId
                            }
                        }) {
                            Span({
                                style {
                                    fontSize(11.px)
                                    color(Color("#dbe7ff"))
                                    backgroundColor(Color("rgba(0,0,0,0.45)"))
                                    padding(3.px, 8.px)
                                    borderRadius(999.px)
                                    opacity(1)
                                }
                            }) {
                                Text(if (isSelected) "#$assetId ausgewählt" else "#$assetId")
                            }
                        }
                    }
                }
            }
        }
    }
}
